import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  listSiteSettings,
  updateSiteSetting,
  AdminApiError,
  TASK_LONG_RUNNING_KEY,
  type AdminSiteSetting,
  type TaskLongRunningSetting,
} from '../../api/adminClient';

/**
 * A site's alert preferences: today, the long-running-task alert (Akash,
 * 2026-10-12: "part of the site's settings ... as the preferences may be
 * different for different sites").
 *
 * The server is the authority on the value and its vocabulary
 * (`site_settings.py`): the task types listed are the ones it sent, and
 * what is shown is the value the site runs on now, its default included.
 * A type's box left blank uses the default limit; 0 means that type is
 * never timed.
 *
 * The alerts engine runs in the operator api-server and re-reads the
 * settings every 30 s, so a save is live within that -- said under the
 * button rather than implied to be instant.
 */

const TYPE_LABELS: Record<string, string> = {
  clean: 'Cleaning',
  patrol: 'Patrol',
  delivery: 'Delivery',
  station_docking: 'Going to a station',
  dustbag: 'Routine check',
  charge: 'Charging',
  custom: 'Other',
};

const MAX_MINUTES = 24 * 60;

interface Draft {
  enabled: boolean;
  defaultMinutes: string;
  /** Per type: '' = use the default. */
  byCategory: Record<string, string>;
}

function draftFrom(value: TaskLongRunningSetting, categories: string[]): Draft {
  const byCategory: Record<string, string> = {};
  for (const c of categories) {
    const v = value.by_category[c];
    byCategory[c] = v === undefined ? '' : String(v);
  }
  return { enabled: value.enabled, defaultMinutes: String(value.default_minutes), byCategory };
}

function wholeMinutes(raw: string, min: number): number | null {
  if (!/^\d+$/.test(raw.trim())) return null;
  const n = Number(raw.trim());
  return n >= min && n <= MAX_MINUTES ? n : null;
}

/** The value to send, or the sentence saying what is wrong with the form. */
function valueFrom(draft: Draft): TaskLongRunningSetting | string {
  const defaultMinutes = wholeMinutes(draft.defaultMinutes, 1);
  if (defaultMinutes === null) return `The default limit must be 1 to ${MAX_MINUTES} minutes.`;
  const byCategory: Record<string, number> = {};
  for (const [c, raw] of Object.entries(draft.byCategory)) {
    if (raw.trim() === '') continue;
    const n = wholeMinutes(raw, 0);
    if (n === null) {
      return `${TYPE_LABELS[c] ?? c} must be blank, 0, or up to ${MAX_MINUTES} minutes.`;
    }
    byCategory[c] = n;
  }
  return { enabled: draft.enabled, default_minutes: defaultMinutes, by_category: byCategory };
}

export function AlertSettingsSection({
  siteId,
  archived,
}: {
  siteId: string;
  archived: boolean;
}) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ['admin', 'site-settings', siteId],
    queryFn: () => listSiteSettings(siteId),
  });
  const setting: AdminSiteSetting | undefined = q.data?.settings.find(
    (s) => s.key === TASK_LONG_RUNNING_KEY,
  );
  const categories = q.data?.task_categories ?? [];
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  // The server's answer replaces the form when the VALUE changes (after a
  // save the stored, normalized value is what the form must show) -- keyed
  // on content, not object identity, so a background refetch does not wipe
  // what the admin is typing.
  const serverKey = setting
    ? JSON.stringify([setting.value, setting.is_default, categories])
    : null;
  useEffect(() => {
    if (setting) setDraft(draftFrom(setting.value, categories));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on serverKey
  }, [serverKey]);

  const save = useMutation({
    mutationFn: (value: TaskLongRunningSetting | null) =>
      updateSiteSetting(siteId, TASK_LONG_RUNNING_KEY, value),
    onSuccess: () => {
      setError(null);
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['admin', 'site-settings', siteId] });
    },
    onError: (err) =>
      setError(err instanceof AdminApiError ? err.message : 'Could not save. Nothing changed.'),
  });

  const formValue = draft ? valueFrom(draft) : null;
  const invalid = typeof formValue === 'string' ? formValue : null;

  return (
    <>
      <h4
        style={{
          marginBottom: 4,
          marginTop: 20,
          fontSize: 12,
          letterSpacing: '0.14em',
          textTransform: 'uppercase',
          color: 'var(--nx-cyan)',
        }}
      >
        Alert settings
      </h4>

      {q.isPending && <p style={{ color: 'var(--nx-muted)', fontSize: 13 }}>Loading…</p>}
      {q.isError && (
        <p style={{ color: 'var(--nx-warning)', fontSize: 13 }}>
          Could not load this site&apos;s alert settings. This is a separate request — nothing
          above is affected, and the site keeps running on its saved settings.
        </p>
      )}

      {setting && draft && (
        <fieldset
          data-testid="long-running-settings"
          disabled={archived || save.isPending}
          style={{ border: '1px solid var(--nx-border)', borderRadius: 4, padding: 12, fontSize: 13 }}
        >
          <legend style={{ padding: '0 4px' }}>Long-running tasks</legend>
          <label style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            <input
              id={`long-running-enabled-${siteId}`}
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => {
                setSaved(false);
                setDraft({ ...draft, enabled: e.target.checked });
              }}
            />
            Alert when a task has been in progress longer than its limit
          </label>
          <p style={{ color: 'var(--nx-muted)', fontSize: 12, margin: '4px 0 10px 24px' }}>
            A warning in the console and in this site&apos;s Chat rooms. It counts paused tasks
            too, and closes when the task ends.
          </p>

          <label style={{ display: 'flex', gap: 8, alignItems: 'baseline' }}>
            Default limit
            <input
              id={`long-running-default-${siteId}`}
              type="number"
              inputMode="numeric"
              min={1}
              max={MAX_MINUTES}
              value={draft.defaultMinutes}
              onChange={(e) => {
                setSaved(false);
                setDraft({ ...draft, defaultMinutes: e.target.value });
              }}
              style={{ width: 80 }}
            />
            minutes
          </label>

          <table style={{ borderCollapse: 'collapse', marginTop: 10 }}>
            <thead>
              <tr style={{ color: 'var(--nx-muted)', textAlign: 'left' }}>
                <th style={{ padding: '4px 12px 4px 0', fontWeight: 400 }}>Task type</th>
                <th style={{ padding: '4px 0', fontWeight: 400 }}>Limit (minutes)</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c) => (
                <tr key={c}>
                  <td style={{ padding: '3px 12px 3px 0' }}>
                    <label htmlFor={`long-running-${c}-${siteId}`}>{TYPE_LABELS[c] ?? c}</label>
                  </td>
                  <td style={{ padding: '3px 0' }}>
                    <input
                      id={`long-running-${c}-${siteId}`}
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={MAX_MINUTES}
                      placeholder={`default (${draft.defaultMinutes || '?'})`}
                      value={draft.byCategory[c] ?? ''}
                      onChange={(e) => {
                        setSaved(false);
                        setDraft({
                          ...draft,
                          byCategory: { ...draft.byCategory, [c]: e.target.value },
                        });
                      }}
                      style={{ width: 120 }}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <p style={{ color: 'var(--nx-muted)', fontSize: 12, margin: '6px 0 0' }}>
            Leave a type blank to use the default. 0 means that type is never timed.
          </p>

          {invalid && (
            <p data-testid="long-running-invalid" style={{ color: 'var(--nx-error)', marginTop: 8 }}>
              {invalid}
            </p>
          )}
          {error && (
            <p data-testid="long-running-error" style={{ color: 'var(--nx-error)', marginTop: 8 }}>
              {error}
            </p>
          )}

          <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', marginTop: 10 }}>
            <button
              type="button"
              disabled={invalid !== null}
              onClick={() => {
                if (formValue && typeof formValue !== 'string') save.mutate(formValue);
              }}
            >
              {save.isPending ? 'Saving…' : 'Save'}
            </button>
            {!setting.is_default && (
              <button
                type="button"
                onClick={() => {
                  if (
                    window.confirm(
                      'Reset to the default? Long-running-task alerts will be off at this site.',
                    )
                  ) {
                    save.mutate(null);
                  }
                }}
              >
                Reset to default
              </button>
            )}
            <span data-testid="long-running-status" style={{ color: 'var(--nx-muted)', fontSize: 12 }}>
              {saved
                ? 'Saved. Live within 30 seconds.'
                : setting.is_default
                  ? 'Not set for this site, so off.'
                  : `Last changed ${setting.updated_at_ms ? new Date(setting.updated_at_ms).toLocaleString() : ''}.`}
            </span>
          </div>
          {archived && (
            <p style={{ color: 'var(--nx-muted)', fontSize: 12, marginTop: 6 }}>
              Decommissioned: re-commission the site to change its settings.
            </p>
          )}
        </fieldset>
      )}
    </>
  );
}
